"use client";

import { useEffect } from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToastStore, type ToastItem } from "@/stores/toast-store";

const VARIANT_STYLES: Record<
  ToastItem["variant"],
  { wrapper: string; icon: React.ComponentType<{ className?: string }> }
> = {
  success: {
    wrapper: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    icon: CheckCircle2,
  },
  error: {
    wrapper: "border-destructive/40 bg-destructive/10 text-destructive",
    icon: AlertTriangle,
  },
  info: {
    wrapper: "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300",
    icon: Info,
  },
};

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      aria-live="polite"
      className="fixed top-4 right-4 z-[1000] flex flex-col gap-2 max-w-md pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastView({ toast }: { toast: ToastItem }) {
  const dismiss = useToastStore((s) => s.dismiss);
  const { wrapper, icon: Icon } = VARIANT_STYLES[toast.variant];

  useEffect(() => {
    if (toast.durationMs === null) return;
    const handle = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(handle);
  }, [toast.id, toast.durationMs, dismiss]);

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-md",
        wrapper
      )}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <span className="flex-1 whitespace-pre-wrap break-words">
        {toast.message}
      </span>
      <button
        onClick={() => dismiss(toast.id)}
        className="opacity-60 hover:opacity-100 transition-opacity shrink-0"
        aria-label="Fermer"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
